import { useRef } from 'react'

import { useMediaQuery } from '@space-core/hooks'
import { useIntersectionObserver } from '@space-core/hooks/useIntersectionObserver'
import { customMedia } from '@space-core/styles'
import { ScreenSizes } from '@space-core/styles'
import useFeatureToggle from '@space-shell/common/hooks/useFeatureToggle'
import type { Section } from '@space-shell/common/services/sections'
import { SectionsEndpoints, SectionType } from '@space-shell/common/services/sections'
import { Toggles } from '@space-shell/common/services/toggle'
import { queryClient } from '@space-shell/config/request'
import { useMembershipStore } from '@space-shell/core/store'
import SectionComponent from '@space-shell/modules/home/components/sectionsInShowcase/section'
import { SectionPlaceholder } from '@space-shell/remotes/preview/components/sectionPlaceholder'
import styled from 'styled-components'

import { DynamicSections } from './section/constants'
import ShowcaseErrorState from './showcaseErrorState'
import ShowcaseLoader from './showcaseLoader'

import LoaderList from './showcaseLoader/loaderList'

import { ADDITIONAL_FOR_PENULTIMATE_POSITION } from '../../constants'
import useGetSections from '../../hooks/useGetSections/useGetSections'
import EmptyClub from '../emptyClub'
import MyProgress from '../myProgress'

const SectionWrapper = styled.div`
  margin-bottom: calc(var(--spacer-8) * 2);
  overflow-x: clip;

  ${customMedia.lessThan('md')`
    margin-bottom: var(--spacer-8);
  `}
`

export type SectionsInShowcaseProps = {
  isPreview?: boolean
}

const SectionsInShowcase = ({ isPreview }: SectionsInShowcaseProps) => {
  const observer = useRef<IntersectionObserver>()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.sm})`)
  const sectionsRequest = useGetSections({ isPreview })
  const { membershipData } = useMembershipStore()
  const { data: isEnableHotmartClubDynamicSections } = useFeatureToggle({
    toggleName: Toggles.EnableHotmartClubDynamicSections
  })

  const membershipId = membershipData?.id ?? ''

  const invalidateGetSections = () =>
    queryClient.invalidateQueries([SectionsEndpoints.sections(membershipId)])

  const penultimateElementRef = useIntersectionObserver({
    observer,
    isLoading: sectionsRequest.isLoading,
    isFetching: sectionsRequest.isFetching,
    fetchNextPage: sectionsRequest.fetchNextPage,
    hasNextPage: Boolean(sectionsRequest.data?.pages?.[0]?.nextCursor)
  })

  if (sectionsRequest.isLoading) return <ShowcaseLoader />

  const hasNoSections = !sectionsRequest.data || !sectionsRequest.data?.pages[0]?.content?.length

  if (sectionsRequest.isError && hasNoSections) {
    return <ShowcaseErrorState onTryAgain={invalidateGetSections} loaderTitle />
  }

  if (hasNoSections) {
    return <EmptyClub isPreview={isPreview} />
  }

  const getSectionToShow = (section: Section) => {
    const isDynamicSection = DynamicSections.includes(section.type)

    if (!isEnableHotmartClubDynamicSections && isDynamicSection) {
      return <div data-test='empty-section' />
    }

    if (section.type === SectionType.PROGRESS) {
      return <MyProgress title={section.title} />
    }

    if (isPreview && section.type === SectionType.PURCHASED) {
      return (
        <SectionPlaceholder
          orientation={section.orientation}
          title={section.title}
          type={section.type}
        />
      )
    }

    return <SectionComponent section={section} isPreview={isPreview} isMobile={isMobile} />
  }

  return (
    <div data-test='sections-in-showcase'>
      {sectionsRequest.data?.pages.map((page) =>
        page.content.map((section, index) => (
          <SectionWrapper
            key={section.id}
            ref={
              page.content.length === index + ADDITIONAL_FOR_PENULTIMATE_POSITION &&
              !sectionsRequest.isError
                ? penultimateElementRef
                : undefined
            }>
            {section.active && getSectionToShow(section)}
          </SectionWrapper>
        ))
      )}

      {sectionsRequest.isError && (
        <ShowcaseErrorState onTryAgain={sectionsRequest.refetch} loaderTitle />
      )}

      {sectionsRequest.isFetching && (
        <LoaderList orientation={sectionsRequest.data.pages[0].content[0].orientation} title />
      )}
    </div>
  )
}

export default SectionsInShowcase
